import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CardSkeleton } from "@/components/Skeletons";
import { track } from "@/lib/analytics";
import {
    CalendarClock, Sparkles, TrendingUp, Sun, MessageCircle, BookOpen,
    CheckCircle2, ArrowRight, HeartPulse,
} from "lucide-react";

/* Dynamic home briefing — "Lane already did the work before you arrived."
   Max 3 cards from /api/home/briefing/{sid}; quiet state when nothing
   meaningful changed. No streaks, no guilt, no manufactured activity. */

const ICONS = {
    appointment: CalendarClock,
    care_event: HeartPulse,
    what_helped: Sparkles,
    pattern: TrendingUp,
    eased: Sun,
    lane_thread: MessageCircle,
    resource: BookOpen,
    quiet: CheckCircle2,
};

function BriefCard({ card, onClick }) {
    const Icon = ICONS[card.kind] || Sparkles;
    const teal = card.tone === "teal";
    const sky = card.tone === "sky";
    return (
        <div
            className={`rounded-3xl p-6 shadow-[0_10px_30px_-18px_rgba(15,76,92,0.35)] ${teal ? "bg-[#12776F] text-white" : sky ? "bg-[#E6F0F2]" : "bg-white"}`}
            data-testid={`briefing-card-${card.kind}`}
        >
            <div className="flex items-start gap-4">
                <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${teal ? "bg-white/15" : "bg-[#12776F]/8"}`}>
                    <Icon size={19} strokeWidth={1.7} className={teal ? "text-[#E3B562]" : "text-[#12776F]"} />
                </div>
                <div className="min-w-0">
                    <div className={`text-[11px] font-semibold tracking-[0.14em] uppercase ${teal ? "text-[#E3B562]" : "text-[#8AA6AD]"}`}>{card.eyebrow}</div>
                    <h3 className={`ml-v2-serif text-lg font-semibold mt-1 ${teal ? "!text-white" : "text-[#12776F]"}`}>{card.title}</h3>
                    <p className={`text-sm mt-1.5 leading-relaxed ${teal ? "text-white/80" : "text-[#4C5551]"}`}>{card.body}</p>
                    {card.cta_label && card.cta_to && (
                        <Link
                            to={card.cta_to}
                            onClick={() => onClick(card)}
                            data-testid={`briefing-cta-${card.kind}`}
                            className={`mt-3 inline-flex items-center gap-1.5 text-sm font-semibold rounded-2xl px-5 py-2 min-h-[44px] transition-colors ${teal ? "bg-[#E3B562] text-[#12776F] hover:bg-[#edc37a]" : "bg-[#12776F] text-white hover:bg-[#14607a]"}`}
                        >
                            {card.cta_label} <ArrowRight size={14} />
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

export const BriefingStack = ({ sid }) => {
    // undefined = still loading (show skeleton) · null/empty = nothing to show
    const [data, setData] = useState(undefined);
    const shownRef = useRef(false);

    useEffect(() => {
        if (!sid) return;
        api.get(`/home/briefing/${sid}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, [sid]);

    useEffect(() => {
        if (data && !shownRef.current) {
            shownRef.current = true;
            track("home_briefing_shown", {
                kinds: (data.cards || []).map((c) => c.kind).join(","),
                quiet: Boolean(data.quiet),
            });
        }
    }, [data]);

    if (data === undefined && sid) {
        return <CardSkeleton lines={2} testid="briefing-skeleton" />;
    }
    if (!data || !(data.cards || []).length) return null;
    const onClick = (card) => track("home_briefing_card_click", { kind: card.kind });

    return (
        <section className="mt-10" data-testid="dashboard-briefing">
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#8AA6AD] mb-3">
                {data.quiet ? "Lane checked while you were away" : "Lane looked at things before you arrived"}
            </div>
            <div className="space-y-4" data-testid="briefing-stack">
                {data.cards.map((c) => <BriefCard key={c.kind} card={c} onClick={onClick} />)}
            </div>
        </section>
    );
};
